import React, { useState, useRef, useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Card, CardContent,
  Avatar, Chip, Button, CircularProgress, Alert, Divider,
  TextField, IconButton, Collapse, Pagination, Tooltip, InputAdornment, FormControlLabel, Switch, Link,
} from '@mui/material';
import { markSeen, useNewCounts } from 'shared/hooks/useNewCounts';

import InterestsNewsCard from 'components/collab/interests/InterestsNewsCard';
import EmojiReactionButton from 'components/common/buttons/EmojiReactionButton';
import { PostShareButton, RepostButton } from 'components/common/PostShare';
import PostStatsButton from 'components/common/PostStats';
import ReportButton from 'components/common/ReportButton';
import { MentionText, useMentions } from 'components/social/feed/mentions';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import InterestComposer, { InterestReplyForm as ReplyForm } from 'components/collab/interests/InterestComposer';
import { postMarkdown } from 'shared/helpers/postText';
import {
  Groups as GroupsIcon,
  Handshake as HandshakeIcon,
  ChatBubbleOutline as ReplyIcon,
  ArrowForward as ArrowForwardIcon,
  CampaignOutlined as CallIcon,
  Campaign as AnnouncementIcon,
  Work as WorkIcon,
  Send as SendIcon,
  OpenInNew as OpenInNewIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { fetcher, axiosService, useUser } from 'shared/helpers/axios';
import {useTheme} from "@mui/material/styles";
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';

const RELEVANT_KEY = 'otable.interests.relevant';
const readRelevant = () => { try { return localStorage.getItem(RELEVANT_KEY) !== '0'; } catch { return true; } };

// ── Constants ─────────────────────────────────────────────────────────────────


const PAGE_SIZE = 12;

const INTEREST_TYPES = {
  all: { label: 'All Announcements', filter: null },
  // keys are the backend's type_of_interest codes (0 = seeking a consortium, 1 = seeking partners);
  // the tabs show Seeking Partners first
  0:   { label: 'Seeking Consortium', icon: <GroupsIcon fontSize="small" />, description: 'Researchers seeking to form a consortium for a funding call' },
  1:   { label: 'Seeking Partners',   icon: <HandshakeIcon fontSize="small" />, description: 'Researchers with a consortium seeking partners' },
  2:   { label: 'Announcements',      icon: <AnnouncementIcon fontSize="small" />, description: 'Important updates and announcements' },
};


// ── Helper: interest chip ─────────────────────────────────────────────────────

function InterestChip({ type }) {
  const theme = useTheme();

  

  

  const info = INTEREST_TYPES[type];
  if (!info || !info.icon) return null;
  return (
    <Chip
      icon={info.icon}
      label={info.label}
      size="small"
      sx={{
        bgcolor: theme.palette.divider, 
        color: "text.primary",
        fontWeight: 300,
        '& .MuiChip-icon': { color: info.color },
      }}
    />
  );
}

// ── Replies section ───────────────────────────────────────────────────────────

function RepliesSection({ commentId, replyCount, refreshParent }) {
  const [open, setOpen] = useState(false);
  const { data: replies, mutate } = useSWR(
    open ? `/interact/comments/${commentId}/replies/` : null,
    fetcher,
  );
  const { user } = useUser();

  const handleReplySubmitted = () => {
    mutate();
    refreshParent();
  };

  if (replyCount === 0 && !open) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        startIcon={<ReplyIcon />}
        onClick={() => setOpen((v) => !v)}
        sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.85rem' }}
      >
        {open ? 'Hide' : `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`}
      </Button>

      <Collapse in={open}>
        <Box sx={{ pl: 2, mt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
          {!replies && <CircularProgress size={14} />}
          {replies?.map((r) => (
            <Box key={r.id} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Avatar src={r.author_info?.avatar} sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'primary.avatar', color: '#fff' }}>
                  {(r.author_info?.first_name?.[0] || '?').toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="caption" fontWeight={600}>
                    {r.author_info?.first_name} {r.author_info?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {r.created && formatDistanceToNow(new Date(r.created), { addSuffix: true })}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}><MentionText text={r.body} /></Typography>
                </Box>
              </Box>
            </Box>
          ))}

          {user && (
            <ReplyForm
              parentId={commentId}
              onSubmitted={handleReplySubmitted}
              onCancel={() => setOpen(false)}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Single announcement card ──────────────────────────────────────────────────

function AnnouncementCard({ comment, onLikeToggle, onRefresh }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const [replyOpen, setReplyOpen] = useState(false);
  // its author edits it in place, in the composer of the call's page
  const isMine = Boolean(user) && String(comment.author_info?.id) === String(user.id);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const saveEdit = async (body, typeOfInterest) => {
    setEditError(null);
    try {
      await axiosService.patch(`/interact/comments/${comment.id}/`, { body, type_of_interest: typeOfInterest });
    } catch (err) {
      setEditError(err.response?.data?.body?.[0] || 'Your changes could not be saved. Please try again.');
      throw err;
    }
    setEditing(false);
    onRefresh();
  };

  const authorName = [comment.author_info?.first_name, comment.author_info?.last_name]
    .filter(Boolean).join(' ') || comment.author_info?.username || 'Unknown';
  const timeAgo = comment.created
    ? formatDistanceToNow(new Date(comment.created), { addSuffix: true })
    : '';


  const handleViewCall = () => {
    if (comment.target_id) navigate(`/funding/call/${comment.target_id}/`);
  };

  const handleViewProfile = () => {
    if (comment.author_info?.id) navigate(`/profile/${comment.author_info.id}/`);
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'card.background',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header: author + interest type */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
            onClick={handleViewProfile}
          >
            <Avatar
              src={comment.author_info?.avatar}
              sx={{ width: 40, height: 40, bgcolor: 'primary.avatar', color: '#fff' }}
            >
              {(comment.author_info?.first_name?.[0] || '?').toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" color="primary.main" fontWeight={700} sx={{ lineHeight: .5 }}>
                {authorName}
              </Typography>
              {comment.author_info?.position && (
                <Typography variant="caption" color="text.primary">
                  {comment.author_info.position}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                {timeAgo}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InterestChip type={comment.type_of_interest} />
            {isMine && !editing && (
              <Tooltip title="Edit">
                <IconButton size="small" aria-label="Edit this interest" onClick={() => setEditing(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Linked call */}
        {comment.target_name && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5,
              bgcolor: 'primary.xulight', borderRadius: 1, px: 1, py: 0.5,
              cursor: 'pointer', width: 'fit-content',
            }}
            onClick={handleViewCall}
          >
            <CallIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {comment.target_name}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 12, color: 'primary.main' }} />
          </Box>
        )}

        {/* Body */}
        {/* written like a post: markdown, with mentions as links */}
        {editing && editError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEditError(null)}>{editError}</Alert>}
        {editing ? (
          <InterestComposer currentUser={user} interest={comment} onSubmit={saveEdit}
            onCancel={() => { setEditing(false); setEditError(null); }} />
        ) : (
        <Box sx={{ mb: 1.5, lineHeight: 1.6, wordBreak: 'break-word', '& p': { mt: 0, mb: 0.75 }, '& p:last-child': { mb: 0 } }}>
          <LabMarkdown images hashtags>{postMarkdown(comment.body)}</LabMarkdown>
        </Box>
        )}

        <Divider sx={{ mb: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* the same reactions, repost, share and stats as posts */}
          <EmojiReactionButton contentType="comment" objectId={comment.id} currentUser={user} size="small" />

          <Button
            size="small"
            startIcon={<ReplyIcon fontSize="small" />}
            onClick={() => setReplyOpen((v) => !v)}
            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.95rem', minWidth: 0 }}
          >
            Reply
          </Button>
          {user && <RepostButton post={comment} interest />}
          <PostShareButton post={comment} interest withLabel />
          {user && String(comment.author_info?.id) === String(user.id) && <PostStatsButton post={comment} interest />}

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={handleViewCall}
            sx={{ color: 'primary.main', textTransform: 'none', fontSize: '0.95rem' }}
          >
            View Call
          </Button>
        </Box>

        {/* Inline reply box */}
        {replyOpen && (
          <ReplyForm
            parentId={comment.id}
            onSubmitted={() => { setReplyOpen(false); onRefresh(); }}
            onCancel={() => setReplyOpen(false)}
          />
        )}

        {/* Replies */}
        {comment.replies_count > 0 && (
          <RepliesSection
            commentId={comment.id}
            replyCount={comment.replies_count}
            refreshParent={onRefresh}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── Announcement post card ────────────────────────────────────────────────────

function PostAnnouncementCard({ post, onLikeToggle, onFlagToggle, onRefresh }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const [flagging, setFlagging] = useState(false);
  const isStaff = user?.is_superuser || user?.is_staff;
  // its author edits its text in place, as a post on the activity feed
  const isMine = Boolean(user) && String(post.author?.id) === String(user.id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const startEdit = () => { setDraft(post.body || ''); setEditError(null); setEditing(true); };
  const saveEdit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    setEditError(null);
    try {
      await axiosService.patch(`/interact/posts/${post.id}/`, { body: draft });
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      setEditError(err.response?.data?.body?.[0] || err.response?.data?.detail || 'Your changes could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const authorName = post.author?.name ||
    [post.author?.first_name, post.author?.last_name].filter(Boolean).join(' ') ||
    post.author?.username || 'Unknown';
  const timeAgo = post.created
    ? formatDistanceToNow(new Date(post.created), { addSuffix: true })
    : '';


  const handleFlag = async () => {
    if (!isStaff || flagging) return;
    setFlagging(true);
    try {
      const action = post.is_flagged ? 'unflag' : 'flag';
      await axiosService.post(`/interact/posts/${post.id}/${action}/`);
      if (onFlagToggle) onFlagToggle(post.id);
    } catch (err) {
      console.error('Flag failed:', err);
    } finally {
      setFlagging(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
        bgcolor: 'card.background', transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
            onClick={() => post.author?.id && navigate(`/profile/${post.author.id}/`)}
          >
            <Avatar src={post.author?.avatar} sx={{ width: 40, height: 40, bgcolor: 'primary.avatar', color: '#fff' }}>
              {(authorName[0] || '?').toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" color="primary.main"  fontWeight={700} sx={{ lineHeight:.5 }}>{authorName}</Typography>
              {post.author?.position && (
                <Typography variant="caption" color="text.primary">{post.author.position}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt:.2 }}>{timeAgo}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InterestChip type="2" />
            {isMine && !editing && (
              <Tooltip title="Edit">
                <IconButton size="small" aria-label="Edit this announcement" onClick={startEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {/* <Chip
            icon={<AnnouncementIcon fontSize="small" />}
            label="Announcement"
            size="small"
            sx={{ bgcolor: 'rgba(230,81,0,0.10)', color: '#e65100', fontWeight: 600, '& .MuiChip-icon': { color: '#e65100' } }}
          /> */}
        </Box>

        {editing ? (
          <Box sx={{ mb: 1.5 }}>
            {editError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEditError(null)}>{editError}</Alert>}
            <TextField fullWidth multiline minRows={3} size="small" value={draft} autoFocus
              onChange={(e) => setDraft(e.target.value)} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="small" variant="contained" disableElevation onClick={saveEdit} disabled={saving || !draft.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {/* Title */}
            {post.title && (
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                {post.title}
              </Typography>
            )}

            {/* Body */}
            {/* markdown, as on the feed and the interest cards above */}
            <Box sx={{ mb: 1.5, lineHeight: 1.6, wordBreak: 'break-word', '& p': { mt: 0, mb: 0.75 }, '& p:last-child': { mb: 0 } }}>
              <LabMarkdown images hashtags>{postMarkdown(post.body)}</LabMarkdown>
            </Box>
          </>
        )}

        {/* A job opening's announcement links to the position */}
        {post.position && (
          <Button size="small" variant="contained" disableElevation startIcon={<WorkIcon />}
            onClick={() => navigate(`/positions/${post.position}/`)} sx={{ mb: 1.5, textTransform: 'none' }}>
            View position and apply
          </Button>
        )}

        <Divider sx={{ mb: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* the same reactions, repost, share and stats as posts in the feed */}
          <EmojiReactionButton contentType="post" objectId={post.id} currentUser={user} size="small" />
          {user && <RepostButton post={post} />}
          <PostShareButton post={post} withLabel />
          {user && String(post.author?.id) === String(user.id) && <PostStatsButton post={post} />}
          {user && String(post.author?.id) !== String(user.id) && (
            <ReportButton targetType="post" targetId={post.id} targetLabel="announcement" sx={{ color: 'text.secondary' }} />
          )}

          <Box sx={{ flex: 1 }} />

          {isStaff && (
            <Tooltip title={post.is_flagged ? 'Remove flag' : 'Flag as irrelevant announcement'}>
              <IconButton
                size="small"
                onClick={handleFlag}
                disabled={flagging}
                sx={{ color: post.is_flagged ? 'error.main' : 'text.disabled', mr: 0.5 }}
              >
                {post.is_flagged ? <FlagIcon fontSize="small" /> : <FlagOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => post.author?.id && navigate(`/profile/${post.author.id}/`)}
            sx={{ color: 'primary.main', textTransform: 'none', fontSize: '0.95rem' }}
          >
            View Profile
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const VALID_TABS = ['0', '1', '2'];

export default function InterestsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(location.search).get('tab');
    return VALID_TABS.includes(p) ? p : '1';   // no tab in the link: the first tab (Seeking Partners, code 1)
  });
  const [page, setPage] = useState(1);
  const [likeOverlay, setLikeOverlay] = useState({});
  const [flagOverlay, setFlagOverlay] = useState({});
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // Only interests on calls that match your profile (the calls of your activity feed); on by default
  const { has_relevance: hasRelevance } = useNewCounts();
  const [onlyRelevantChoice, setOnlyRelevantChoice] = useState(readRelevant);
  const onlyRelevant = onlyRelevantChoice && hasRelevance;
  const toggleRelevant = (on) => {
    setOnlyRelevantChoice(on); setPage(1);
    try { localStorage.setItem(RELEVANT_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  };
  useEffect(() => { markSeen('interests'); }, []);     // the sidebar's "new" chip starts again from now

  useEffect(() => {
    const p = new URLSearchParams(location.search).get('tab');
    const next = VALID_TABS.includes(p) ? p : '1';
    setTab(next);
    setPage(1);
    setLikeOverlay({});
    setSearch('');
    setSearchInput('');
  }, [location.search]);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    setPage(1);
    setLikeOverlay({});
    setSearch('');
    setSearchInput('');
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const buildKey = useCallback(() => {
    if (tab === '2') {
      const params = new URLSearchParams({ is_announcement: 'true', is_flagged: 'false', ordering: '-created', page, page_size: PAGE_SIZE });
      if (search) params.set('search', search);
      return `/interact/posts/?${params.toString()}`;
    }
    const params = new URLSearchParams({
      target_type: 'call',
      ordering: '-created',
      page,
      page_size: PAGE_SIZE,
      is_flagged: 'false',
    });
    if (tab !== 'all') params.set('type_of_interest', tab);
    if (search) params.set('search', search);
    if (onlyRelevant) params.set('relevant', '1');
    return `/interact/comments/?${params.toString()}`;
  }, [tab, page, search, onlyRelevant]);

  const { data, isLoading, error, mutate } = useSWR(buildKey(), fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  const items = data?.results ?? (Array.isArray(data) ? data : []);
  const totalCount = data?.count ?? items.length;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const handleLikeToggle = useCallback((itemId) => {
    setLikeOverlay((prev) => {
      const wasLiked = prev[itemId] !== undefined
        ? prev[itemId].is_liked
        : items.find((c) => c.id === itemId)?.is_liked;
      return { ...prev, [itemId]: { is_liked: !wasLiked } };
    });
  }, [items]);

  const handleFlagToggle = useCallback((itemId) => {
    setFlagOverlay((prev) => {
      const wasFlagged = prev[itemId] !== undefined
        ? prev[itemId].is_flagged
        : items.find((c) => c.id === itemId)?.is_flagged;
      return { ...prev, [itemId]: { is_flagged: !wasFlagged } };
    });
  }, [items]);

  const mergedItems = items.map((c) => ({
    ...c,
    ...(likeOverlay[c.id] ?? {}),
    ...(flagOverlay[c.id] ?? {}),
    like_count: likeOverlay[c.id]?.is_liked !== undefined
      ? c.like_count + (likeOverlay[c.id].is_liked ? 1 : -1)
      : c.like_count,
  }));

  // For interest tabs (not announcements) only show items linked to a resolvable call
  const displayItems = tab === '2'
    ? mergedItems
    : mergedItems.filter((item) => item.target_id && item.target_name);

  return (
    <PageContainer>
      {/* Header */}
      {/* tabs after the title; "relevant to me" and the search at the right: the Collaboration Hub's header */}
      <PageHeader title="Interests"
        subtitle="Researchers expressing interest in collaborating on active funding calls. Engage directly or view the call for more context."
        actions={(
          <>
          {/* between the tabs and the search: only interests on calls that match your profile */}
          {tab !== '2' && hasRelevance && (
            <Tooltip title="Only interests on calls that match the skills and research interests on your profile, the same calls as in your activity feed">
              <FormControlLabel sx={{ m: 0, mr: 1, whiteSpace: 'nowrap' }}
                control={<Switch size="small" checked={onlyRelevantChoice} onChange={(e) => toggleRelevant(e.target.checked)} />}
                label={<Typography variant="body2">Relevant to me</Typography>} />
            </Tooltip>
          )}
          <TextField
            size="small"
            placeholder="Search…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ width: 240 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchInput(''); setSearch(''); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          </>
        )}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ ...HEADER_TABS_SX, minWidth: 0 }}
          >
            {/* <Tab label="All" value="all"  /> */}

            <Tab
              label={
                <Tooltip title={INTEREST_TYPES[1].description} placement="bottom">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HandshakeIcon fontSize="small" /> Seeking Partners
                  </Box>
                </Tooltip>
              }
              value="1"
            />

            <Tab
              label={
                <Tooltip title={INTEREST_TYPES[0].description} placement="bottom">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupsIcon fontSize="small" /> Seeking Consortium
                  </Box>
                </Tooltip>
              }
              value="0"
            />

            <Tab
              label={
                <Tooltip title={INTEREST_TYPES[2].description} placement="bottom">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AnnouncementIcon fontSize="small" /> Announcements
                  </Box>
                </Tooltip>
              }
              value="2"
            />
          </Tabs>
      </PageHeader>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: 3, mt: 2, alignItems: 'flex-start' }}>

        {/* Left — main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {tab !== '2' && hasRelevance === false && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Add skills and research interests to <Link component="button" onClick={() => navigate(`/profile/${user?.id || ''}/`)} sx={{ verticalAlign: 'baseline' }}>your profile</Link> and this page
              can show only the interests on calls that concern you. Until then you see all of them.
            </Alert>
          )}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load. Please try again.
            </Alert>
          )}

          {!isLoading && !error && displayItems.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              {tab === '2'
                ? <AnnouncementIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                : <GroupsIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
              }
              <Typography variant="h6" color="text.secondary">
                {tab !== '2' && onlyRelevant ? 'Nothing relevant to you yet' : 'Nothing here yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {tab === '2'
                  ? 'Posts marked as announcements from your activity feed will appear here.'
                  : onlyRelevant
                    ? 'Nobody has posted an interest on the calls that match your profile yet.'
                    : 'When researchers express interest in funding calls, their announcements appear here.'
                }
              </Typography>
              {tab !== '2' && onlyRelevant && (
                <Button size="small" sx={{ mt: 1.5 }} onClick={() => toggleRelevant(false)}>Show all interests</Button>
              )}
            </Box>
          )}

          {tab === '2'
            ? displayItems.map((post) => (
                <PostAnnouncementCard
                  key={post.id}
                  post={post}
                  onLikeToggle={handleLikeToggle}
                  onFlagToggle={handleFlagToggle}
                  onRefresh={mutate}
                />
              ))
            : displayItems.map((comment) => (
                <AnnouncementCard
                  key={comment.id}
                  comment={comment}
                  onLikeToggle={handleLikeToggle}
                  onRefresh={mutate}
                />
              ))
          }

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
               <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_, v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  color="primary"
               />
            </Box>
          )}
        </Box>
        {/* Right — news card sidebar */}
        <Box sx={{ width: 300, flexShrink: 0, pt: 4, display: { xs: 'none', md: 'block' } }}>
          <InterestsNewsCard height={350} />
        </Box>

      </Box>
    </PageContainer>
  );
}
